import { FavouriteDataAdapter } from '../../favourite/common/favourite-data-adapter';
import { FavouriteDataColumn, AggregationType } from '../../favourite/common/favourite-interfaces';
import { TableFavouriteModelComponent } from '../table-favourite-model-component';
import { AggregatedField, RegularField } from '../types';
import { DataRow } from './types';
import { mapCell } from './cell';
import { shouldDisplayTotal } from './display-total';

const calculateSubtotal = (dataAdapter: FavouriteDataAdapter,
  report: number,
  columns: FavouriteDataColumn[],
  row: DataRow[]) => {

  const { aggrColIndex } = dataAdapter;

  const aggrType = columns[aggrColIndex].aggregationType;

  if (!shouldDisplayTotal(dataAdapter, columns, report)) {
    return undefined;
  }

  return row.reduce((memo, currentRow) => {

    const aggregatedValue = currentRow.row[aggrColIndex].value;

    if (memo === undefined && aggregatedValue !== undefined) {
      return aggregatedValue;
    }

    if (!aggregatedValue) {
      return memo;
    }

    switch (aggrType) {
      case AggregationType.Count:
      case AggregationType.Total:
        return memo + aggregatedValue;

      case AggregationType.Minimum:
      case AggregationType.DaysSinceLast:
        return aggregatedValue < memo ? aggregatedValue : memo;

      case AggregationType.Maximum:
      case AggregationType.DaysSinceFirst:
        return aggregatedValue > memo ? aggregatedValue : memo;

      default:
        return memo;
    }
  }, undefined);
};

export const mapAggregateValues = (dataAdapter: FavouriteDataAdapter,
  model: TableFavouriteModelComponent,
  columns: FavouriteDataColumn[],
  data: DataRow[],
  column: number): AggregatedField => {

  const { aggrColIndex } = dataAdapter;

  return {
    tooltip: model.getTooltip(aggrColIndex),
    label: model.getHeaderTitle(column),
    values: data.map(r => mapCell(dataAdapter, model, columns, r, column, aggrColIndex)),
    columnType: columns[column].columnType
  };
};

export const mapSubvalue = (dataAdapter: FavouriteDataAdapter,
  model: TableFavouriteModelComponent,
  columns: FavouriteDataColumn[],
  data: DataRow[],
  column: number): RegularField => {

  const { aggrColIndex } = dataAdapter;

  const subValue = calculateSubtotal(dataAdapter, model.dataModel.report, columns, data);

  const onClick = !!model.drillDownHandler ? () => {
    model.drillDownHandler.toCaseListing(data[0].index, [column]);
  } : undefined;

  return subValue !== undefined ? {
    tooltip: model.getTooltip(aggrColIndex) + ':\n' + model.format(aggrColIndex, subValue),
    label: model.getHeaderTitle(aggrColIndex),
    onClick,
    value: model.format(aggrColIndex, subValue),
    columnType: columns[aggrColIndex].columnType
  } : undefined;
};

export const mapRollingAverage = (dataAdapter: FavouriteDataAdapter,
  model: TableFavouriteModelComponent,
  columns: FavouriteDataColumn[],
  data: DataRow[],
  column: number): AggregatedField => {

  const { aggrRollAvgColIndex } = dataAdapter;

  return aggrRollAvgColIndex >= 0 ? {
    tooltip: model.getTooltip(aggrRollAvgColIndex),
    label: model.getHeaderTitle(aggrRollAvgColIndex),
    values: data.map(r => mapCell(dataAdapter, model, columns, r, column, aggrRollAvgColIndex)),
    columnType: columns[aggrRollAvgColIndex].columnType
  } : undefined;
};

export const mapTarget = (dataAdapter: FavouriteDataAdapter,
  model: TableFavouriteModelComponent,
  columns: FavouriteDataColumn[],
  data: DataRow[],
  column: number): AggregatedField => {

  const { targetColIndex } = dataAdapter;

  return targetColIndex >= 0 ? {
    tooltip: model.getTooltip(targetColIndex),
    label: model.getHeaderTitle(targetColIndex),
    values: data.map(r => mapCell(dataAdapter, model, columns, r, column, targetColIndex)),
    columnType: columns[targetColIndex].columnType
  } : undefined;
};
